import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, MoveHorizontal } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';

const documents = [
  { file: 'Lyncia-Post-Training-r15.html', zh: '训练', en: 'Training' },
  { file: 'Lyncia-Omni-Harness-Architecture.html', zh: '部署', en: 'Deployment' },
  { file: 'Lyncia-Evaluation-Overview.html', zh: '评测', en: 'Evaluation' },
];

function OriginalDocument({ file, title }: { file: string; title: string }) {
  const { t } = useI18n();
  const ref = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(0);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  useEffect(() => {
    const frame = ref.current;
    const body = frame?.contentDocument?.body;
    const viewport = frame?.contentDocument?.getElementById('viewport');
    if (!frame || !body || !viewport) return;
    const updateScroll = () => setCanScroll({ left: viewport.scrollLeft > 1, right: viewport.scrollLeft < viewport.scrollWidth - viewport.clientWidth - 1 });
    const resize = () => {
      frame.style.height = `${Math.ceil(body.getBoundingClientRect().height) + 2}px`;
      updateScroll();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(body);
    observer.observe(viewport);
    viewport.addEventListener('scroll', updateScroll, { passive: true });
    resize();
    return () => { observer.disconnect(); viewport.removeEventListener('scroll', updateScroll); };
  }, [loaded]);
  const pan = (direction: number) => {
    const viewport = ref.current?.contentDocument?.getElementById('viewport');
    if (!viewport) return;
    viewport.scrollBy({ left: direction * viewport.clientWidth * .8, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  return <div className="lp-model-document">
    <div className="lp-diagram-pan" role="group" aria-label={t(`${title}图导航`, `${title} diagram navigation`)}>
      <span><MoveHorizontal size={15} />{t('左右滑动', 'Swipe to explore')}</span>
      <button type="button" disabled={!canScroll.left} onClick={() => pan(-1)} aria-label={t(`向左查看${title}图`, `Pan ${title} diagram left`)}><ChevronLeft size={18} /></button>
      <button type="button" disabled={!canScroll.right} onClick={() => pan(1)} aria-label={t(`向右查看${title}图`, `Pan ${title} diagram right`)}><ChevronRight size={18} /></button>
    </div>
    <iframe ref={ref} className="lp-stacked-frame" title={title} src={`${import.meta.env.BASE_URL}model-documents/${file}`} onLoad={() => setLoaded(value => value + 1)} />
  </div>;
}

export function ModelsPage(_props: { startHref: string }) {
  const { t, locale } = useI18n();
  return <main className="lp-stacked-models" id="landing-content" tabIndex={-1}>
    <div className="lp-stacked-inner">
      <header className="lp-model-summary">
        <h1>Lyncia-Counselor-Omni-R1</h1>
        <p>{t('面向连续多次咨询的多模态模型，理解当下的表达，也连接过往的会谈。', 'A multimodal model for ongoing counseling, connecting what you share now with earlier sessions.')}</p>
        <div>{[t('多模态交流', 'Multimodal conversation'), t('跨会谈记忆', 'Cross-session memory'), t('Thinker × Talker × Harness', 'Thinker × Talker × Harness')].map(label => <span key={label}>{label}</span>)}</div>
      </header>
      <nav className="lp-stacked-nav" aria-label={t('模型介绍', 'Model overview')}>
        {documents.map((doc, index) => <button key={doc.file} onClick={() => document.getElementById(`model-section-${index}`)?.scrollIntoView({ block: 'start' })}>{t(doc.zh, doc.en)}</button>)}
      </nav>
      {documents.map((doc, index) => <section key={doc.file} id={`model-section-${index}`} className="lp-stacked-section" aria-labelledby={`model-title-${index}`}>
        <h2 id={`model-title-${index}`}><span>0{index + 1}</span>{t(doc.zh, doc.en)}</h2>
        <OriginalDocument key={locale} file={locale === 'en' ? doc.file.replace('.html', '.en.html') : doc.file} title={t(doc.zh, doc.en)} />
      </section>)}
    </div>
  </main>;
}
